import type { AuthProvider } from '@refinedev/core';
import { authClient, getAccessToken, clearTokens } from './axios';
import { clearPermissionsCache } from './permissionsCache';

export interface UserIdentity {
  id: string;
  email: string;
  name: string;
  picture?: string;
  roles: string[];
  permissions: string[];
  /**
   * Companies the person's customer permissions are limited to, merged across their role
   * assignments the same way the auth service builds the token: null means every customer.
   */
  customerScope: string[] | null;
}

/** Same merge as sf-auth-service utils/jwt.ts: any unlimited assignment means every customer. */
export function mergeCustomerScope(
  assignments: Array<{ customerScope?: string[] | null }> | undefined,
): string[] | null {
  const merged: string[] = [];
  for (const a of assignments ?? []) {
    if (a.customerScope === null || a.customerScope === undefined) return null;
    for (const c of a.customerScope) if (!merged.includes(c)) merged.push(c);
  }
  return merged;
}

export const authProvider: AuthProvider = {
  login: async () => {
    try {
      const { data } = await authClient.post('/auth/google', {
        redirectUri: `${window.location.origin}/auth/callback`,
      });
      const authUrl = data.authUrl || data.data?.authUrl;
      if (!authUrl) {
        return {
          success: false,
          error: {
            name: 'LoginError',
            message: 'No auth URL returned from server',
          },
        };
      }
      window.location.href = authUrl;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          name: 'LoginError',
          message: 'Failed to initiate login',
        },
      };
    }
  },

  logout: async () => {
    clearTokens();
    clearPermissionsCache();
    return { success: true, redirectTo: '/login' };
  },

  check: async () => {
    const token = getAccessToken();
    if (!token) {
      return { authenticated: false, redirectTo: '/login' };
    }

    try {
      await authClient.get('/auth/me');
      return { authenticated: true };
    } catch {
      return { authenticated: false, redirectTo: '/login' };
    }
  },

  getPermissions: async () => {
    try {
      const { data } = await authClient.get('/auth/me');
      const user = data.user || data.data?.user || data;
      return user.permissions || [];
    } catch {
      return [];
    }
  },

  getIdentity: async (): Promise<UserIdentity | null> => {
    try {
      const { data } = await authClient.get('/auth/me');
      const user = data.user || data.data?.user || data;
      return {
        id: user.id || user._id,
        email: user.email,
        name: user.name || user.email,
        picture: user.picture,
        roles: user.roles || [],
        permissions: user.permissions || [],
        customerScope: mergeCustomerScope(user.roleAssignments),
      };
    } catch {
      return null;
    }
  },

  onError: async (error) => {
    const status = error?.statusCode || error?.response?.status;
    if (status === 401) {
      return { logout: true, redirectTo: '/login' };
    }
    return { error };
  },
};
